<script setup lang="ts">
import { h } from 'vue'
import { useI18n } from 'vue-i18n'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import tooltipDirective from '@lib/directives/tooltip'
import useThemeVars from '@lib/composables/useThemeVars'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'

const props = defineProps<{
  hideCopy?: boolean
  hideEdit?: boolean
  hideDelete?: boolean
  hideViewDetails?: boolean
  hideViewNewTab?: boolean
  isCopying: boolean
  isDeleting: boolean
}>()

const emit = defineEmits<{
  copy: []
  edit: []
  delete: []
  view: []
  viewNewTab: []
  viewDetails: []
  duplicate: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const FONT_SIZE_CLASS = 'font-size-4'

function getActionsMenu() {
  const actionsMenu: DropdownMixedOption[] = [
    {
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.sections.CSectionManagement.actions.view')),
      key: 'view',
      icon: () => h(CIcon, { icon: 'eye' }),
    },
  ]
  if (!props.hideViewDetails)
    actionsMenu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.sections.CSectionManagement.actions.viewDetails')),
      key: 'viewDetails',
      icon: () => h(CIcon, { icon: 'circle-info' }),
    })

  if (!props.hideEdit) {
    actionsMenu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.sections.CSectionManagement.actions.edit')),
      key: 'edit',
      icon: () => h(CIcon, { icon: 'edit' }),
    })
  }

  if (!props.hideViewNewTab) {
    actionsMenu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.sections.CSectionManagement.actions.viewNewTab')),
      key: 'viewNewTab',
      icon: () => h(CIcon, { icon: 'browsers' }),
    })
  }

  if (!props.hideCopy || !props.hideDelete) {
    actionsMenu.push({ key: 'd1', type: 'divider' })
  }

  if (!props.hideCopy) {
    actionsMenu.push({
      label: () =>
        h('div', { class: FONT_SIZE_CLASS }, t('canvasUI.components.sections.CSectionManagement.actions.duplicate')),
      key: 'copy',
      icon: () => h(CIcon, { icon: 'copy' }),
    })
  }
  if (!props.hideDelete) {
    actionsMenu.push({
      label: () =>
        h(
          'div',
          { class: FONT_SIZE_CLASS, style: { color: themeVars.value.error } },
          t('canvasUI.components.sections.CSectionManagement.actions.delete'),
        ),
      key: 'delete',
      icon: () => h(CIcon, { icon: 'trash', color: themeVars.value.error }),
    })
  }

  return actionsMenu
}

function handleActions(key: string) {
  switch (key) {
    case 'view':
      emit('view')
      break
    case 'viewNewTab':
      emit('viewNewTab')
      break
    case 'viewDetails':
      emit('viewDetails')
      break
    case 'edit':
      emit('edit')
      break
    case 'copy':
      emit('copy')
      break
    case 'delete':
      emit('delete')
      break
    default:
      break
  }
}
</script>

<template>
  <div class="flex justify-end gap-2">
    <CButton
      v-if="!hideCopy"
      v-tooltip="t('canvasUI.components.sections.CSectionManagement.actions.buttons.tooltips.duplicate')"
      circle
      :loading="isCopying"
      @click="handleActions('copy')"
    >
      <template #icon>
        <CIcon icon="copy" size="16px" />
      </template>
    </CButton>

    <CButton
      v-if="!hideDelete"
      v-tooltip="t('canvasUI.components.sections.CSectionManagement.actions.buttons.tooltips.delete')"
      circle
      :loading="isDeleting"
      secondary
      type="error"
      @click="handleActions('delete')"
    >
      <template #icon>
        <CIcon icon="trash" size="16px" />
      </template>
    </CButton>

    <CDropdown :options="getActionsMenu()" trigger="click" @select="handleActions">
      <CButton v-tooltip="t('canvasUI.components.sections.CSectionManagement.actions.buttons.tooltips.actions')" circle>
        <template #icon>
          <CIcon icon="ellipsis" size="16px" />
        </template>
      </CButton>
    </CDropdown>
  </div>
</template>
